import { Handle, Position } from 'reactflow';
import { Mail, MessageSquare, Phone, Clock, GitBranch, Webhook, Play } from 'lucide-react';

// Direct Mail Node (LettrLabs)
export function DirectMailNode({ data, selected }: NodeProps) {
  return (
    <div
      className={`px-4 py-3 shadow-lg rounded-lg border-2 bg-white ${
        selected ? 'border-blue-500' : 'border-gray-300'
      }`}
    >
      <Handle type="target" position={Position.Top} className="w-3 h-3 bg-teal-500" />
      <div className="flex items-center gap-2">
        <div className="w-8 h-8 rounded-full bg-teal-100 flex items-center justify-center">
          <Mail className="w-4 h-4 text-teal-700" />
        </div>
        <div>
          <div className="text-xs text-gray-500 uppercase">Direct Mail</div>
          <div className="font-semibold">{data.label}</div>
        </div>
      </div>
      <Handle type="source" position={Position.Bottom} className="w-3 h-3 bg-teal-500" />
    </div>
  );
}

interface NodeData {
  label: string;
  type: string;
  config?: any;
}

interface NodeProps {
  data: NodeData;
  selected?: boolean;
}

// Trigger Node
export function TriggerNode({ data, selected }: NodeProps) {
  return (
    <div className={`px-4 py-3 shadow-lg rounded-lg border-2 bg-white ${
      selected ? 'border-blue-500' : 'border-gray-300'
    }`}>
      <div className="flex items-center gap-2">
        <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center">
          <Play className="w-4 h-4 text-green-600" />
        </div>
        <div>
          <div className="text-xs text-gray-500 uppercase">Trigger</div>
          <div className="font-semibold">{data.label}</div>
        </div>
      </div>
      <Handle type="source" position={Position.Bottom} className="w-3 h-3 bg-green-500" />
    </div>
  );
}

// Email Node
export function EmailNode({ data, selected }: NodeProps) {
  return (
    <div className={`px-4 py-3 shadow-lg rounded-lg border-2 bg-white ${
      selected ? 'border-blue-500' : 'border-gray-300'
    }`}>
      <Handle type="target" position={Position.Top} className="w-3 h-3 bg-blue-500" />
      <div className="flex items-center gap-2">
        <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center">
          <Mail className="w-4 h-4 text-blue-600" />
        </div>
        <div>
          <div className="text-xs text-gray-500 uppercase">Email</div>
          <div className="font-semibold">{data.label}</div>
        </div>
      </div>
      <Handle type="source" position={Position.Bottom} className="w-3 h-3 bg-blue-500" />
    </div>
  );
}

// SMS Node
export function SMSNode({ data, selected }: NodeProps) {
  return (
    <div className={`px-4 py-3 shadow-lg rounded-lg border-2 bg-white ${
      selected ? 'border-blue-500' : 'border-gray-300'
    }`}>
      <Handle type="target" position={Position.Top} className="w-3 h-3 bg-purple-500" />
      <div className="flex items-center gap-2">
        <div className="w-8 h-8 rounded-full bg-purple-100 flex items-center justify-center">
          <MessageSquare className="w-4 h-4 text-purple-600" />
        </div>
        <div>
          <div className="text-xs text-gray-500 uppercase">SMS</div>
          <div className="font-semibold">{data.label}</div>
        </div>
      </div>
      <Handle type="source" position={Position.Bottom} className="w-3 h-3 bg-purple-500" />
    </div>
  );
}

// AI Call Node
export function AICallNode({ data, selected }: NodeProps) {
  return (
    <div className={`px-4 py-3 shadow-lg rounded-lg border-2 bg-white ${
      selected ? 'border-blue-500' : 'border-gray-300'
    }`}>
      <Handle type="target" position={Position.Top} className="w-3 h-3 bg-orange-500" />
      <div className="flex items-center gap-2">
        <div className="w-8 h-8 rounded-full bg-orange-100 flex items-center justify-center">
          <Phone className="w-4 h-4 text-orange-600" />
        </div>
        <div>
          <div className="text-xs text-gray-500 uppercase">AI Call</div>
          <div className="font-semibold">{data.label}</div>
        </div>
      </div>
      <Handle type="source" position={Position.Bottom} className="w-3 h-3 bg-orange-500" />
    </div>
  );
}

// Delay Node
export function DelayNode({ data, selected }: NodeProps) {
  return (
    <div className={`px-4 py-3 shadow-lg rounded-lg border-2 bg-white ${
      selected ? 'border-blue-500' : 'border-gray-300'
    }`}>
      <Handle type="target" position={Position.Top} className="w-3 h-3 bg-gray-500" />
      <div className="flex items-center gap-2">
        <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center">
          <Clock className="w-4 h-4 text-gray-600" />
        </div>
        <div>
          <div className="text-xs text-gray-500 uppercase">Wait</div>
          <div className="font-semibold">{data.label}</div>
        </div>
      </div>
      <Handle type="source" position={Position.Bottom} className="w-3 h-3 bg-gray-500" />
    </div>
  );
}

// Condition Node
export function ConditionNode({ data, selected }: NodeProps) {
  return (
    <div className={`px-4 py-3 shadow-lg rounded-lg border-2 bg-white ${
      selected ? 'border-blue-500' : 'border-gray-300'
    }`}>
      <Handle type="target" position={Position.Top} className="w-3 h-3 bg-yellow-500" />
      <div className="flex items-center gap-2">
        <div className="w-8 h-8 rounded-full bg-yellow-100 flex items-center justify-center">
          <GitBranch className="w-4 h-4 text-yellow-600" />
        </div>
        <div>
          <div className="text-xs text-gray-500 uppercase">Condition</div>
          <div className="font-semibold">{data.label}</div>
        </div>
      </div>
      <Handle type="source" position={Position.Bottom} id="true" className="w-3 h-3 bg-green-500" style={{ left: '30%' }} />
      <Handle type="source" position={Position.Bottom} id="false" className="w-3 h-3 bg-red-500" style={{ left: '70%' }} />
    </div>
  );
}

// Webhook Node
export function WebhookNode({ data, selected }: NodeProps) {
  return (
    <div className={`px-4 py-3 shadow-lg rounded-lg border-2 bg-white ${
      selected ? 'border-blue-500' : 'border-gray-300'
    }`}>
      <Handle type="target" position={Position.Top} className="w-3 h-3 bg-indigo-500" />
      <div className="flex items-center gap-2">
        <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center">
          <Webhook className="w-4 h-4 text-indigo-600" />
        </div>
        <div>
          <div className="text-xs text-gray-500 uppercase">Webhook</div>
          <div className="font-semibold">{data.label}</div>
        </div>
      </div>
      <Handle type="source" position={Position.Bottom} className="w-3 h-3 bg-indigo-500" />
    </div>
  );
}

// Node type mapping
export const nodeTypes = {
  trigger: TriggerNode,
  email: EmailNode,
  direct_mail: DirectMailNode,
  sms: SMSNode,
  ai_call: AICallNode,
  delay: DelayNode,
  condition: ConditionNode,
  webhook: WebhookNode,
};
